/**
 * Serviço de Gestão do Ciclo de Vida e Status das Reservas.
 * Define o fluxo rigoroso e padronizado:
 * PENDING (4 horas para confirmação do atleta) -> CONFIRMED -> FINALIZED (ou CANCELLED)
 */

import { getPendingReservationTimeoutMinutes } from "../../utils/commissionConfig";

export type BookingStatus =
  | "PENDING"
  | "CONFIRMED"
  | "FINALIZED"
  | "CANCELLED"
  | "NO_SHOW"
  | "CRIADA"
  | "AGUARDANDO_PIX"
  | "PIX_GERADO"
  | "PAGO"
  | "CONFIRMADO"
  | "VOUCHER_EMITIDO"
  | "CHECK_IN"
  | "EM_ANDAMENTO"
  | "FINALIZADA"
  | "ARQUIVADA"
  | "CANCELADA"
  | "REEMBOLSADA";

export interface BookingStatusConfig {
  key: string;
  label: string;
  badgeBg: string;
  textColor: string;
  borderColor: string;
  description: string;
}

export const BOOKING_STATUS_MAP: Record<string, BookingStatusConfig> = {
  PENDING: {
    key: "PENDING",
    label: "Pendente de Confirmação",
    badgeBg: "bg-amber-500/15",
    textColor: "text-amber-400",
    borderColor: "border-amber-500/40",
    description: "Aguardando pagamento/confirmação dentro do prazo de 5 minutos."
  },
  PENDENTE: {
    key: "PENDING",
    label: "Pendente de Confirmação",
    badgeBg: "bg-amber-500/15",
    textColor: "text-amber-400",
    borderColor: "border-amber-500/40",
    description: "Aguardando pagamento/confirmação dentro do prazo de 5 minutos."
  },
  CONFIRMED: {
    key: "CONFIRMED",
    label: "Confirmado",
    badgeBg: "bg-emerald-500/15",
    textColor: "text-emerald-400",
    borderColor: "border-emerald-500/30",
    description: "Agendamento confirmado. Vaga e horário garantidos na quadra."
  },
  CONFIRMADO: {
    key: "CONFIRMED",
    label: "Confirmado",
    badgeBg: "bg-emerald-500/15",
    textColor: "text-emerald-400",
    borderColor: "border-emerald-500/30",
    description: "Agendamento confirmado. Vaga e horário garantidos na quadra."
  },
  FINALIZED: {
    key: "FINALIZED",
    label: "Finalizado",
    badgeBg: "bg-zinc-800/90",
    textColor: "text-zinc-400",
    borderColor: "border-zinc-700",
    description: "Horário de uso da quadra concluído."
  },
  FINALIZADO: {
    key: "FINALIZED",
    label: "Finalizado",
    badgeBg: "bg-zinc-800/90",
    textColor: "text-zinc-400",
    borderColor: "border-zinc-700",
    description: "Horário de uso da quadra concluído."
  },
  FINALIZADA: {
    key: "FINALIZED",
    label: "Finalizado",
    badgeBg: "bg-zinc-800/90",
    textColor: "text-zinc-400",
    borderColor: "border-zinc-700",
    description: "Horário de uso da quadra concluído."
  },
  PRESENTE: {
    key: "PRESENTE",
    label: "Finalizado • Presente",
    badgeBg: "bg-teal-500/15",
    textColor: "text-teal-400",
    borderColor: "border-teal-500/40",
    description: "Presença confirmada no check-in da arena."
  },
  AUSENTE: {
    key: "AUSENTE",
    label: "Finalizado • Ausente",
    badgeBg: "bg-orange-500/15",
    textColor: "text-orange-400",
    borderColor: "border-orange-500/40",
    description: "Atleta ausente no check-in da arena."
  },
  UTILIZADO: {
    key: "FINALIZED",
    label: "Finalizado • Utilizado",
    badgeBg: "bg-zinc-800/90",
    textColor: "text-zinc-400",
    borderColor: "border-zinc-700",
    description: "Voucher utilizado e validado na arena."
  },
  CANCELLED: {
    key: "CANCELLED",
    label: "Cancelado",
    badgeBg: "bg-rose-500/15",
    textColor: "text-rose-400",
    borderColor: "border-rose-500/30",
    description: "Agendamento cancelado ou expirado por ausência de confirmação."
  },
  CANCELADA: {
    key: "CANCELLED",
    label: "Cancelado",
    badgeBg: "bg-rose-500/15",
    textColor: "text-rose-400",
    borderColor: "border-rose-500/30",
    description: "Agendamento cancelado ou expirado por ausência de confirmação."
  },
  EXPIRED: {
    key: "CANCELLED",
    label: "Cancelado por Timeout",
    badgeBg: "bg-rose-500/15",
    textColor: "text-rose-400",
    borderColor: "border-rose-500/30",
    description: "Agendamento cancelado automaticamente por expiração do prazo de pagamento (5 min)."
  },
  EXPIRADO: {
    key: "CANCELLED",
    label: "Cancelado por Timeout",
    badgeBg: "bg-rose-500/15",
    textColor: "text-rose-400",
    borderColor: "border-rose-500/30",
    description: "Agendamento cancelado automaticamente por expiração do prazo de pagamento (5 min)."
  },
  EXPIRADA: {
    key: "CANCELLED",
    label: "Cancelado por Timeout",
    badgeBg: "bg-rose-500/15",
    textColor: "text-rose-400",
    borderColor: "border-rose-500/30",
    description: "Agendamento cancelado automaticamente por expiração do prazo de pagamento (5 min)."
  },
  AGUARDANDO_PAGAMENTO: {
    key: "PENDING",
    label: "Aguardando Pagamento",
    badgeBg: "bg-amber-500/15",
    textColor: "text-amber-400",
    borderColor: "border-amber-500/40",
    description: "Aguardando pagamento Pix dentro do prazo de 5 minutos."
  },
  AGUARDANDO_CONFIRMACAO: {
    key: "PENDING",
    label: "Aguardando Confirmação",
    badgeBg: "bg-amber-500/15",
    textColor: "text-amber-400",
    borderColor: "border-amber-500/40",
    description: "Aguardando confirmação dentro do prazo estipulado."
  },
  NO_SHOW: {
    key: "NO_SHOW",
    label: "Não Compareceu (No-Show)",
    badgeBg: "bg-orange-500/15",
    textColor: "text-orange-400",
    borderColor: "border-orange-500/40",
    description: "Atleta não compareceu ao horário agendado."
  },
  "NO-SHOW": {
    key: "NO_SHOW",
    label: "Não Compareceu (No-Show)",
    badgeBg: "bg-orange-500/15",
    textColor: "text-orange-400",
    borderColor: "border-orange-500/40",
    description: "Atleta não compareceu ao horário agendado."
  },
  NAO_COMPARECEU: {
    key: "NO_SHOW",
    label: "Não Compareceu (No-Show)",
    badgeBg: "bg-orange-500/15",
    textColor: "text-orange-400",
    borderColor: "border-orange-500/40",
    description: "Atleta não compareceu ao horário agendado."
  },
  CRIADA: {
    key: "CRIADA",
    label: "Criada",
    badgeBg: "bg-zinc-800",
    textColor: "text-zinc-300",
    borderColor: "border-zinc-700",
    description: "Reserva iniciada pelo atleta."
  },
  AGUARDANDO_PIX: {
    key: "AGUARDANDO_PIX",
    label: "Aguardando Pix",
    badgeBg: "bg-amber-950/60",
    textColor: "text-amber-400",
    borderColor: "border-amber-800/80",
    description: "Aguardando solicitação de chave/código Pix."
  },
  PIX_GERADO: {
    key: "PIX_GERADO",
    label: "Pix Gerado",
    badgeBg: "bg-amber-500/10",
    textColor: "text-amber-300",
    borderColor: "border-amber-500/30",
    description: "Código Pix copia e cola gerado. Aguardando pagamento."
  },
  PAGO: {
    key: "PAGO",
    label: "Pago",
    badgeBg: "bg-blue-500/10",
    textColor: "text-blue-400",
    borderColor: "border-blue-500/30",
    description: "Pagamento confirmado pelo gateway ou arena."
  },
  VOUCHER_EMITIDO: {
    key: "VOUCHER_EMITIDO",
    label: "Voucher Emitido",
    badgeBg: "bg-purple-500/10",
    textColor: "text-purple-300",
    borderColor: "border-purple-500/30",
    description: "QR Code e Voucher de entrada gerados para o atleta."
  },
  CHECK_IN: {
    key: "CHECK_IN",
    label: "Check-in Realizado",
    badgeBg: "bg-teal-500/10",
    textColor: "text-teal-300",
    borderColor: "border-teal-500/30",
    description: "Entrada validada na recepção da arena via QR Code."
  },
  EM_ANDAMENTO: {
    key: "EM_ANDAMENTO",
    label: "Em Andamento",
    badgeBg: "bg-indigo-500/10",
    textColor: "text-indigo-300",
    borderColor: "border-indigo-500/30",
    description: "Horário em utilização pelo atleta."
  },
  ARQUIVADA: {
    key: "ARQUIVADA",
    label: "Arquivada",
    badgeBg: "bg-zinc-950",
    textColor: "text-zinc-500",
    borderColor: "border-zinc-900",
    description: "Reserva armazenada no histórico."
  },
  REEMBOLSADA: {
    key: "REEMBOLSADA",
    label: "Reembolsada",
    badgeBg: "bg-amber-500/10",
    textColor: "text-amber-400",
    borderColor: "border-amber-500/30",
    description: "Valor estornado ao atleta."
  }
};

/**
 * Verifica se há evidência monetária real e comprovável de pagamento prévio.
 * Considera exclusivamente valores monetários efetivamente persistidos (paidAmount,
 * amountPaid, valorPago, depositAmount, advanceAmount, advancePayment, remainingPaidAmount,
 * financialSnapshot.* e sinalPago numérico > 0).
 * Nunca considera timestamps (paidAt, paymentReceivedAt, paymentConfirmedAt, remainingPaidAt),
 * identificadores de transação (paymentId, gatewayPaymentId, transactionId) isolados ou com status,
 * grossAmount, price, totalPrice, refundAmount, refundProcessed, refundRecord, cancellationOutcome,
 * status "REFUNDED"/"REEMBOLSADA", paymentStatus "estornado", status "CONFIRMED" ou checkInStatus "PRESENTE".
 */
export function hasRealPaymentEvidence(
  data?: any,
  _paymentReceivedAt?: string,
  _paymentStatus?: string
): boolean {
  if (!data || typeof data !== "object") return false;
  const raw = data?.rawItem || data?.raw || data?.slot || data;
  const snap =
    data?.financialSnapshot ||
    data?.financialModelSnapshot ||
    raw?.financialSnapshot ||
    raw?.financialModelSnapshot;

  const numericSignals = [
    data?.paidAmount,
    raw?.paidAmount,
    snap?.paidAmount,
    data?.amountPaid,
    raw?.amountPaid,
    snap?.amountPaid,
    data?.valorPago,
    raw?.valorPago,
    snap?.valorPago,
    data?.depositAmount,
    raw?.depositAmount,
    snap?.depositAmount,
    data?.advanceAmount,
    raw?.advanceAmount,
    snap?.advanceAmount,
    data?.advancePayment,
    raw?.advancePayment,
    snap?.advancePayment,
    data?.remainingPaidAmount,
    raw?.remainingPaidAmount,
    snap?.remainingPaidAmount,
    typeof data?.sinalPago === "number" ? data.sinalPago : 0,
    typeof raw?.sinalPago === "number" ? raw.sinalPago : 0,
    typeof snap?.sinalPago === "number" ? snap.sinalPago : 0
  ];

  return numericSignals.some((v) => Number(v || 0) > 0);
}

const REFUND_CLAIMED_STATUSES = new Set([
  "REEMBOLSADA",
  "REEMBOLSADO",
  "REFUNDED",
  "ESTORNADO",
  "ESTORNADA",
  "ESTORNO",
  "REFUND_PROCESSED"
]);

const CANCELLED_OR_REFUND_STATUSES = new Set([
  "CANCELLED",
  "CANCELADA",
  "CANCELADO",
  "CANCELLED_NO_REFUND",
  "REEMBOLSADA",
  "REEMBOLSADO",
  "REFUNDED",
  "ESTORNADO",
  "ESTORNADA",
  "ESTORNO",
  "REFUND_PENDING",
  "REFUND_PROCESSED",
  "EXPIRED",
  "EXPIRADO",
  "EXPIRADA"
]);

/**
 * Normaliza qualquer status bruto para as 4 categorias primárias:
 * PENDING, CONFIRMED, FINALIZED, CANCELLED
 */
export function normalizeBookingCategory(
  status?: string | any,
  paymentStatus?: string,
  expiresAt?: string,
  createdAt?: string,
  confirmationDeadline?: string,
  paymentReceivedAt?: string,
  checkInStatus?: string,
  bookingData?: any
): "PENDING" | "CONFIRMED" | "FINALIZED" | "CANCELLED" {
  const isObjectArg = typeof status === "object" && status !== null;
  const bookingObj = isObjectArg ? status : bookingData;

  const effectiveStatus = isObjectArg
    ? (bookingObj.status ?? bookingObj.bookingStatus ?? "")
    : (status ?? bookingObj?.status ?? bookingObj?.bookingStatus ?? "");
  const effectivePaymentStatus = paymentStatus ?? bookingObj?.paymentStatus ?? "";
  const effectiveExpiresAt = expiresAt ?? bookingObj?.expiresAt;
  const effectiveCreatedAt = createdAt ?? bookingObj?.createdAt;
  const effectiveConfirmationDeadline = confirmationDeadline ?? bookingObj?.confirmationDeadline;
  const effectivePaymentReceivedAt = paymentReceivedAt ?? bookingObj?.paymentReceivedAt;
  const effectiveCheckInStatus = checkInStatus ?? bookingObj?.checkInStatus;

  const rawStatus = String(effectiveStatus || "").toUpperCase().trim();
  const rawPayment = String(effectivePaymentStatus || "").toUpperCase().trim();
  const rawCheckIn = String(effectiveCheckInStatus || "").toUpperCase().trim();
  const rawBookingStatus = String(bookingObj?.bookingStatus || bookingObj?.rawStatus || "").toUpperCase().trim();
  const rawOutcome = String(bookingObj?.cancellationOutcome || bookingObj?.rawItem?.cancellationOutcome || "").toUpperCase().trim();

  if (
    bookingObj?.isCancelled === true ||
    bookingObj?.rawItem?.isCancelled === true ||
    Boolean(bookingObj?.cancelledAt || bookingObj?.canceledAt || bookingObj?.cancellationDate) ||
    Boolean(bookingObj?.cancellationReason || bookingObj?.cancellationOrigin || bookingObj?.cancelledBy) ||
    CANCELLED_OR_REFUND_STATUSES.has(rawStatus) ||
    CANCELLED_OR_REFUND_STATUSES.has(rawPayment) ||
    CANCELLED_OR_REFUND_STATUSES.has(rawBookingStatus) ||
    CANCELLED_OR_REFUND_STATUSES.has(rawOutcome) ||
    rawCheckIn === "CANCELADO" ||
    rawCheckIn === "CANCELADA" ||
    rawCheckIn === "CANCELLED"
  ) {
    return "CANCELLED";
  }

  if (
    rawCheckIn === "PRESENTE" ||
    rawCheckIn === "AUSENTE" ||
    rawCheckIn === "UTILIZADO" ||
    rawCheckIn === "NO_SHOW" ||
    rawStatus === "PRESENTE" ||
    rawStatus === "AUSENTE" ||
    rawStatus === "FINALIZED" ||
    rawStatus === "FINALIZADA" ||
    rawStatus === "FINALIZADO" ||
    rawStatus === "CONCLUIDA" ||
    rawStatus === "CONCLUIDO" ||
    rawStatus === "UTILIZADO" ||
    rawStatus === "NO_SHOW" ||
    rawStatus === "NO-SHOW" ||
    rawStatus === "NAO_COMPARECEU"
  ) {
    return "FINALIZED";
  }

  if (
    rawStatus === "CONFIRMED" ||
    rawStatus === "CONFIRMADO" ||
    rawStatus === "CONFIRMADA" ||
    rawStatus === "MARCADA" ||
    rawStatus === "MARCADO" ||
    rawStatus === "VOUCHER_EMITIDO" ||
    rawStatus === "CHECK_IN" ||
    rawStatus === "EM_ANDAMENTO" ||
    rawStatus === "PAGO" ||
    rawStatus === "SINAL_PAGO" ||
    rawPayment === "NAO_APLICAVEL" ||
    rawPayment === "ISENTO" ||
    rawPayment === "PAGO" ||
    rawPayment === "PAID" ||
    rawPayment === "RECEIVED" ||
    rawPayment === "CONFIRMED" ||
    rawPayment === "CONFIRMADO" ||
    rawPayment === "SINAL_PAGO" ||
    Boolean(effectivePaymentReceivedAt)
  ) {
    return "CONFIRMED";
  }

  // Verifica se o agendamento em estado pendente (e não pago) já estourou o prazo de expiração (5 min)
  if (effectiveExpiresAt || effectiveCreatedAt) {
    const remainingMs = calculateRemainingConfirmationMs(
      effectiveConfirmationDeadline,
      undefined,
      effectiveExpiresAt,
      effectiveCreatedAt
    );
    if (remainingMs <= 0) {
      return "CANCELLED";
    }
  }

  if (
    rawStatus === "PENDING" ||
    rawStatus === "PENDENTE" ||
    rawStatus === "PENDING_CONFIRMATION" ||
    rawStatus === "AGUARDANDO_CONFIRMACAO" ||
    rawStatus === "AGUARDANDO_PAGAMENTO" ||
    rawStatus === "AGUARDANDO_PIX" ||
    rawStatus === "PIX_GERADO" ||
    rawStatus === "CRIADA"
  ) {
    return "PENDING";
  }

  return "PENDING";
}

/**
 * Retorna as informações visuais formatadas para qualquer status
 */
export function getBookingStatusConfig(
  status?: string | any,
  paymentStatus?: string,
  checkInStatus?: string,
  bookingData?: any
): BookingStatusConfig {
  const isObjectArg = typeof status === "object" && status !== null;
  const bookingObj = isObjectArg ? status : bookingData;

  const effectiveStatus = isObjectArg
    ? (bookingObj.status ?? bookingObj.bookingStatus ?? "")
    : (status ?? bookingObj?.status ?? bookingObj?.bookingStatus ?? "");
  const effectivePaymentStatus = paymentStatus ?? bookingObj?.paymentStatus ?? "";
  const effectiveCheckInStatus = checkInStatus ?? bookingObj?.checkInStatus ?? "";

  const rawStatus = String(effectiveStatus || "").toUpperCase().trim();
  const rawPayment = String(effectivePaymentStatus || "").toUpperCase().trim();
  const rawCheckIn = String(effectiveCheckInStatus || "").toUpperCase().trim();
  const rawBookingStatus = String(bookingObj?.bookingStatus || bookingObj?.rawStatus || "").toUpperCase().trim();
  const rawOutcome = String(bookingObj?.cancellationOutcome || bookingObj?.rawItem?.cancellationOutcome || "").toUpperCase().trim();
  const rawRefundStatus = String(
    bookingObj?.refundStatus ||
    bookingObj?.refundRecord?.status ||
    bookingObj?.rawItem?.refundRecord?.status ||
    ""
  ).toUpperCase().trim();

  const isCancelledContext =
    rawStatus === "CANCELLED" ||
    rawStatus === "CANCELADO" ||
    rawStatus === "CANCELADA" ||
    rawStatus === "REFUND_PENDING" ||
    rawBookingStatus === "CANCELLED" ||
    rawBookingStatus === "CANCELADO" ||
    rawBookingStatus === "REFUND_PENDING" ||
    rawOutcome === "REFUND_PENDING" ||
    Boolean(bookingObj?.isCancelled) ||
    Boolean(bookingObj?.cancelledAt) ||
    Boolean(bookingObj?.canceledAt);

  const isRefundPending =
    rawStatus === "REFUND_PENDING" ||
    rawPayment === "REFUND_PENDING" ||
    rawBookingStatus === "REFUND_PENDING" ||
    rawOutcome === "REFUND_PENDING" ||
    rawRefundStatus === "REFUND_PENDING" ||
    (isCancelledContext && rawRefundStatus === "PENDING") ||
    (isCancelledContext && bookingObj?.refundProcessed === false && !REFUND_CLAIMED_STATUSES.has(rawStatus));

  // REFUND_PENDING é operacionalmente CANCELLED (nunca reembolso concluído)
  if (isRefundPending) {
    return BOOKING_STATUS_MAP.CANCELLED;
  }

  const hasRefundClaim =
    REFUND_CLAIMED_STATUSES.has(rawStatus) ||
    REFUND_CLAIMED_STATUSES.has(rawPayment) ||
    REFUND_CLAIMED_STATUSES.has(rawBookingStatus) ||
    REFUND_CLAIMED_STATUSES.has(rawOutcome) ||
    bookingObj?.isRefunded === true ||
    bookingObj?.refundProcessed === true ||
    Number(bookingObj?.refundAmount || bookingObj?.refundedAmount || bookingObj?.refundRecord?.amount || 0) > 0;

  if (hasRefundClaim) {
    const hasRealPayment = hasRealPaymentEvidence(
      bookingObj,
      bookingObj?.paymentReceivedAt,
      effectivePaymentStatus
    );

    const isFullRetentionWithoutRefund =
      bookingObj &&
      Number(bookingObj.retainedAmount || 0) > 0 &&
      bookingObj.refundAmount !== undefined &&
      Number(bookingObj.refundAmount || 0) <= 0;

    if (hasRealPayment && !isFullRetentionWithoutRefund) {
      return BOOKING_STATUS_MAP.REEMBOLSADA;
    }

    return BOOKING_STATUS_MAP.CANCELLED;
  }

  const category = normalizeBookingCategory(
    effectiveStatus,
    effectivePaymentStatus,
    bookingObj?.expiresAt,
    bookingObj?.createdAt,
    bookingObj?.confirmationDeadline,
    bookingObj?.paymentReceivedAt,
    effectiveCheckInStatus,
    bookingObj
  );

  if (category === "CANCELLED") {
    if (rawStatus === "EXPIRED" || rawStatus === "EXPIRADO" || rawStatus === "EXPIRADA") {
      return BOOKING_STATUS_MAP[rawStatus];
    }
    if (rawPayment === "EXPIRED" || rawPayment === "EXPIRADO" || rawPayment === "EXPIRADA") {
      return BOOKING_STATUS_MAP.EXPIRED;
    }
    return BOOKING_STATUS_MAP.CANCELLED;
  }

  if (rawCheckIn && (rawCheckIn === "PRESENTE" || rawCheckIn === "AUSENTE") && BOOKING_STATUS_MAP[rawCheckIn]) {
    return BOOKING_STATUS_MAP[rawCheckIn];
  }
  if (BOOKING_STATUS_MAP[rawStatus]) {
    return BOOKING_STATUS_MAP[rawStatus];
  }
  return BOOKING_STATUS_MAP[category] || BOOKING_STATUS_MAP.PENDING;
}

/**
 * Calcula os milissegundos restantes para a expiração do prazo de confirmação/pagamento (TTL de 5 minutos por padrão)
 */
export function calculateRemainingConfirmationMs(
  confirmationDeadline?: string,
  paymentReceivedAt?: string,
  expiresAt?: string,
  createdAt?: string
): number {
  const now = Date.now();

  // 1. Se tem expiresAt explícito do PIX/reserva (prioridade máxima)
  if (expiresAt) {
    const expTime = new Date(expiresAt).getTime();
    if (!isNaN(expTime)) {
      return Math.max(0, expTime - now);
    }
  }

  // 2. Se tem confirmationDeadline explícito
  if (confirmationDeadline) {
    const deadlineTime = new Date(confirmationDeadline).getTime();
    if (!isNaN(deadlineTime)) {
      return Math.max(0, deadlineTime - now);
    }
  }

  // 3. Fallback: createdAt + TTL configurado (padrão 5 minutos) para itens não pagos
  if (createdAt) {
    const createdTime = new Date(createdAt).getTime();
    if (!isNaN(createdTime)) {
      const timeoutMs = (getPendingReservationTimeoutMinutes() || 5) * 60 * 1000;
      const computedDeadline = createdTime + timeoutMs;
      return Math.max(0, computedDeadline - now);
    }
  }

  return 0;
}

/**
 * Formata os milissegundos em string HH:MM:SS
 */
export function formatRemainingTime(ms: number): string {
  if (ms <= 0) return "00:00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [
    hours.toString().padStart(2, "0"),
    minutes.toString().padStart(2, "0"),
    seconds.toString().padStart(2, "0")
  ].join(":");
}

/**
 * Verifica se uma reserva confirmada já passou da data/hora de término e deve ser finalizada
 */
export function isBookingPastEndTime(dateStr: string, timeStr: string, durationMinutes: number = 60): boolean {
  try {
    const [year, month, day] = dateStr.split("-").map(Number);
    const [hours, minutes] = timeStr.split(":").map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hours) || isNaN(minutes)) {
      return false;
    }
    const slotDate = new Date(year, month - 1, day, hours, minutes, 0);
    const endTime = new Date(slotDate.getTime() + durationMinutes * 60 * 1000);
    return Date.now() > endTime.getTime();
  } catch {
    return false;
  }
}

/**
 * Calcula a data limite exata de confirmação adicionando minutos a partir da data de início
 */
export function calculateConfirmationDeadline(startDate: Date = new Date(), customHours?: number): string {
  const minutes = customHours !== undefined ? customHours * 60 : (getPendingReservationTimeoutMinutes() || 120);
  return new Date(startDate.getTime() + minutes * 60 * 1000).toISOString();
}

/**
 * Verifica se o prazo limite de confirmação/pagamento expirou
 */
export function isBookingExpired(
  confirmationDeadline?: string,
  paymentReceivedAt?: string,
  expiresAt?: string,
  createdAt?: string
): boolean {
  return calculateRemainingConfirmationMs(confirmationDeadline, paymentReceivedAt, expiresAt, createdAt) <= 0;
}

/**
 * Verifica se um agendamento confirmado já finalizou seu horário de jogo
 */
export function shouldFinalizeBooking(dateStr: string, timeStr: string, durationMinutes: number = 60): boolean {
  return isBookingPastEndTime(dateStr, timeStr, durationMinutes);
}

/**
 * Classificador Central de Documento de Reserva Real vs Slot Estrutural no Firestore (/bookings)
 * 
 * Uma Reserva Real possui sinais de identificação do atleta/cliente, estado de agendamento,
 * estado financeiro, voucher/ticket code, ou histórico de checkout/cancelamento.
 * Um Slot Estrutural possui apenas definições de horário/disponibilidade da grade física da quadra.
 */
export function isRealCourtBookingDocument(data: any, documentId?: string): boolean {
  if (!data || typeof data !== "object") return false;

  // 1. Rejeição explícita de intenções de pagamento / pré-registros de checkout
  // Um documento criado exclusivamente para representar uma tentativa/intenção de pagamento NÃO é uma reserva oficial.
  // Documentos com status cancelado explícito são sempre mantidos para garantir liberação e convergência da grade.
  const isCancelled =
    data.isCancelled === true ||
    data.status === "CANCELLED" ||
    data.status === "cancelada" ||
    data.status === "cancelado" ||
    data.bookingStatus === "CANCELLED" ||
    data.bookingStatus === "CANCELADO";

  const rawStatusUpper = String(data.status || data.bookingStatus || "").trim().toUpperCase();
  const rawPaymentUpper = String(data.paymentStatus || "").trim().toUpperCase();

  // Precedência autoritativa: se a reserva possui confirmação/pagamento explícito com evidência (paymentId, confirmedAt, paidAt, etc.)
  const isExplicitlyConfirmed =
    (rawStatusUpper === "CONFIRMED" ||
      rawStatusUpper === "CONFIRMADO" ||
      rawStatusUpper === "CONFIRMADA" ||
      rawStatusUpper === "PAGO" ||
      rawPaymentUpper === "PAGO" ||
      rawPaymentUpper === "RECEIVED" ||
      rawPaymentUpper === "CONFIRMED") &&
    Boolean(
      data.paymentId ||
      data.confirmedAt ||
      data.paidAt ||
      data.paymentConfirmedAt ||
      data.ticketCode ||
      data.voucherCode
    );

  if (
    !isCancelled &&
    !isExplicitlyConfirmed &&
    (data.recordType === "payment_intent" ||
      data.isPaymentIntent === true ||
      data.purpose === "payment_intent" ||
      data.type === "payment_intent" ||
      data.documentType === "payment_intent")
  ) {
    return false;
  }

  // Identificação defensiva de pré-registro de pagamento temporário / legado:
  const effectiveDocId = String(documentId || data.id || data.bookingId || "").trim();
  const isPreBookingId = effectiveDocId.startsWith("booking_") && !effectiveDocId.startsWith("booking_real_");
  const isAguardandoPagamento =
    rawStatusUpper === "AGUARDANDO_PAGAMENTO" ||
    rawPaymentUpper === "AGUARDANDO_PAGAMENTO" ||
    rawStatusUpper === "AGUARDANDO_PIX" ||
    rawPaymentUpper === "AGUARDANDO_PIX";
  const hasNoValidationSignals =
    !data.ticketCode &&
    !data.voucherCode &&
    !data.confirmedAt &&
    !data.checkInStatus &&
    !data.validatedAt &&
    !data.paidAt &&
    !data.paymentConfirmedAt &&
    !data.paymentId;
  const isUnconfirmedPreRecord = !isExplicitlyConfirmed && isPreBookingId && isAguardandoPagamento && hasNoValidationSignals;

  if (isUnconfirmedPreRecord) {
    return false;
  }

  // Projeções sanitizadas de ocupação pública (/court_occupancy) são válidas para ocupação da grade
  if (data.isOccupancyProjection === true || data.type === "court_occupancy" || data.type === "occupancy") {
    if (data.occupied === false || data.status === "CANCELLED" || data.bookingStatus === "CANCELLED") {
      return false;
    }
    return true;
  }

  // 1. Rejeição explícita se for marcado como template estrutural ou disponibilidade pura
  if (
    data.isStructural === true ||
    data.isSlotTemplate === true ||
    data.isStructuralSlot === true ||
    data.type === "structural_slot" ||
    data.type === "slot_template" ||
    data.type === "slot_definition" ||
    data.type === "availability" ||
    data.type === "availability_slot"
  ) {
    return false;
  }

  // 2. Sinais de Atleta / Cliente Responsável
  const athleteIdStr = String(data.athleteId || data.athleteUid || data.userId || "").trim();
  const hasValidAthleteId =
    athleteIdStr !== "" &&
    athleteIdStr !== "null" &&
    athleteIdStr !== "undefined" &&
    !athleteIdStr.startsWith("court_") &&
    !athleteIdStr.startsWith("arena_virtual_");

  const rawBookedBy = String(data.bookedBy || data.athleteName || data.customerName || data.clientName || data.payerName || data.responsibleName || "").trim();
  const rawBookedByLower = rawBookedBy.toLowerCase();
  const hasValidAthleteName =
    rawBookedBy !== "" &&
    rawBookedByLower !== "disponível" &&
    rawBookedByLower !== "disponivel" &&
    rawBookedByLower !== "livre" &&
    rawBookedByLower !== "aberto" &&
    rawBookedByLower !== "slot" &&
    rawBookedByLower !== "vago" &&
    rawBookedByLower !== "grade" &&
    rawBookedByLower !== "horario";

  const hasContactInfo = Boolean(
    (data.bookedCpf && String(data.bookedCpf).trim() !== "") ||
    (data.athleteCpf && String(data.athleteCpf).trim() !== "") ||
    (data.bookedPhone && String(data.bookedPhone).trim() !== "") ||
    (data.athletePhone && String(data.athletePhone).trim() !== "") ||
    (data.bookedEmail && String(data.bookedEmail).trim() !== "") ||
    (data.athleteEmail && String(data.athleteEmail).trim() !== "")
  );

  const hasAthleteSignals = hasValidAthleteId || hasValidAthleteName || hasContactInfo;

  // 3. Sinais de Status de Agendamento (Booking / Status Operacional)
  const rawBookingStatus = String(data.bookingStatus || "").trim().toUpperCase();
  const rawStatus = String(data.status || "").trim().toLowerCase();

  const recognizedBookingStatuses = new Set([
    "CONFIRMED", "CONFIRMADO", "PENDING", "PENDENTE", "FINALIZED", "FINALIZADO",
    "FINALIZADA", "CANCELLED", "CANCELADO", "CANCELADA", "NO_SHOW", "CRIADA",
    "AGUARDANDO_PIX", "PIX_GERADO", "PAGO", "VOUCHER_EMITIDO", "CHECK_IN",
    "EM_ANDAMENTO", "REEMBOLSADA", "EXPIRED", "EXPIRADO", "EXPIRADA", "PRESENTE", "AUSENTE"
  ]);

  const recognizedStatuses = new Set([
    "marcada", "marcado", "reservada", "reservado", "confirmada", "confirmado",
    "pendente", "pago", "finalizada", "finalizado", "cancelada", "cancelado",
    "bloqueado", "manutencao", "aula", "mensalista", "utilizado", "no_show", "expirado"
  ]);

  const hasRecognizedBookingStatus = recognizedBookingStatuses.has(rawBookingStatus);
  const hasRecognizedStatus = recognizedStatuses.has(rawStatus);

  // Status de slot puramente livre sem nenhum outro sinal
  if (
    (rawStatus === "disponivel" || rawStatus === "livre" || rawStatus === "aberto" || rawStatus === "available" || rawStatus === "liberada") &&
    !hasAthleteSignals &&
    !data.ticketCode &&
    !data.paymentStatus &&
    !data.financialModel
  ) {
    return false;
  }

  // 4. Sinais Financeiros / Modelo Comercial
  const rawPaymentStatus = String(data.paymentStatus || "").trim().toLowerCase();
  const hasValidPaymentStatus = [
    "nao_aplicavel", "pago", "pendente", "aguardando_balcao", "estornado",
    "processando", "falhou", "paid", "pending", "refunded", "cancelled",
    "parcial", "cortesia", "isento"
  ].includes(rawPaymentStatus);

  const hasFinancialSignals = Boolean(
    hasValidPaymentStatus ||
    data.financialModel ||
    data.financialModelApplied ||
    data.financialSnapshot ||
    data.financialModelSnapshot ||
    (data.paymentId && String(data.paymentId).trim() !== "") ||
    (data.paymentGateway && String(data.paymentGateway).trim() !== "") ||
    (data.paidAmount !== undefined && data.paidAmount !== null && Number(data.paidAmount) > 0)
  );

  // 5. Sinais de Ticket / Voucher / Check-in
  const hasTicketSignals = Boolean(
    (data.ticketCode && String(data.ticketCode).trim() !== "") ||
    (data.voucherCode && String(data.voucherCode).trim() !== "") ||
    (data.checkInStatus && String(data.checkInStatus).trim() !== "") ||
    data.checkedIn === true ||
    data.isNoShow === true
  );

  // 6. Metadados de Criação / Transação da Reserva
  const hasBookingMetadata = Boolean(
    data.createdAt ||
    data.bookedAt ||
    data.confirmedAt ||
    data.cancelledAt ||
    data.bookingId ||
    data.batchId
  );

  // Combinações Determinantes:
  // A) Atleta identificado + (Status de Agendamento OU Financeiro OU Ticket OU Metadados de Criação)
  if (hasAthleteSignals && (hasRecognizedBookingStatus || hasRecognizedStatus || hasFinancialSignals || hasTicketSignals || hasBookingMetadata)) {
    return true;
  }

  // B) Ticket/Voucher emitido + (Status de Agendamento OU Financeiro)
  if (hasTicketSignals && (hasRecognizedBookingStatus || hasRecognizedStatus || hasFinancialSignals)) {
    return true;
  }

  // C) Modelo Financeiro Aplicado / Snapshot + (Status de Agendamento OU Pagamento)
  if (hasFinancialSignals && (hasRecognizedBookingStatus || hasRecognizedStatus || hasTicketSignals)) {
    return true;
  }

  // D) Status explícito de Reserva Confirmada/Cancelada/Pendente + (PaymentStatus OU Ticket OU Atleta)
  if (hasRecognizedBookingStatus && (hasValidPaymentStatus || hasTicketSignals || hasAthleteSignals)) {
    return true;
  }

  return false;
}

